import { API_BASE_URL } from './apiBase';

export interface FileState {
  exists: boolean;
  path?: string;
  sizeBytes?: number;
  mtime?: string;
  count: number;
  error?: string;
  singleton: boolean;
}

export interface SnapshotEntry {
  snapshotId: string;
  fileName: string;
  filePath?: string;
  createdAt: string;
  sizeBytes: number;
  count: number;
  reason: string;
  sourceSnapshotId?: string;
  sourceSnapshotCreatedAt?: string | null;
  sourceType?: string;
  sourcePath?: string;
}

export interface BackupCollectionState {
  mongoCollection: string;
  singleton: boolean;
  mongoCount: number | null;
  filePrimary: FileState;
  fileBackup: FileState;
  snapshots: SnapshotEntry[];
}

export interface BackupStatusResponse {
  timestamp: string;
  storage: 'mongodb' | 'file';
  mongoConnected: boolean;
  dataDir: string;
  retentionLimit: number;
  lastMongoConnectionAttemptAt: string | null;
  lastMongoConnectionError: {
    message: string;
    code: string | null;
    name: string | null;
    timestamp: string;
  } | null;
  collections: Record<string, BackupCollectionState>;
  restorePhraseHint: string;
}

export interface RestoreCollectionResponse {
  success: boolean;
  collectionKey: string;
  mongoCollection?: string;
  restoredCount: number;
  sourcePath?: string;
  sourceType?: 'backup' | 'primary';
  warnings?: string[];
  snapshotId?: string;
  sourceSnapshot?: SnapshotEntry;
  snapshot?: {
    snapshotId: string;
    count: number;
    sizeBytes: number;
    createdAt: string;
  } | null;
  restoreContext?: {
    sourceVersionLabel?: string;
    sourceType?: 'backup' | 'primary';
    sourcePath?: string;
    sourceCount?: number;
    sourceSnapshotId?: string;
    sourceSnapshotReason?: string;
    sourceSnapshotCreatedAt?: string | null;
    preMongoCount?: number;
    postMongoCount?: number;
    createdSnapshotId?: string | null;
    createdSnapshotAt?: string | null;
  };
  restoredAt: string;
  message?: string;
  error?: string;
}

export const getBackupStatus = async (): Promise<BackupStatusResponse> => {
  const response = await fetch(`${API_BASE_URL}/backups/status`, { cache: 'no-store' });
  if (!response.ok) {
    throw new Error(`Failed to load backup status (${response.status})`);
  }
  return response.json();
};

export const restoreCollection = async (
  collectionKey: string,
  confirmPhrase: string
): Promise<RestoreCollectionResponse> => {
  const response = await fetch(`${API_BASE_URL}/backups/restore-collection`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ collectionKey, confirmPhrase }),
  });

  const body = await response.json();
  if (!response.ok) {
    throw new Error(body?.message || `Restore failed (${response.status})`);
  }
  return body;
};

export const restoreSnapshot = async (
  collectionKey: string,
  snapshotId: string,
  confirmPhrase: string
): Promise<RestoreCollectionResponse> => {
  const response = await fetch(`${API_BASE_URL}/backups/restore-snapshot`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ collectionKey, snapshotId, confirmPhrase }),
  });

  const body = await response.json();
  if (!response.ok) {
    throw new Error(body?.message || `Snapshot restore failed (${response.status})`);
  }
  return body;
};

